import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { RichTextMarkdownPaste } from "@modules/ui/components/richText/richTextMardownPaste"
import { RichTextMenuBar } from "@modules/ui/components/richText/richTextMenuBar"
import Link from "@tiptap/extension-link"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { forwardRef, useImperativeHandle } from "react"

type HURichTextInputProps = {
  label?: string
  isRequiredInput?: boolean
  value: string
  onChange: (value: string) => void
  maxLength: number
  isInvalid: boolean
  error?: React.JSX.Element
  paragrapheFontStyle: "BL" | "BM" | "BS" | "BXS"
  placeholder?: string
} & React.ComponentProps<"div">

export type HURichTextInputRef = {
  forceNewContent: (content: string) => void
}

export const HURichTextInput = forwardRef<HURichTextInputRef, HURichTextInputProps>(
  (
    {
      label,
      value = "",
      isRequiredInput,
      onChange,
      maxLength,
      paragrapheFontStyle,
      isInvalid,
      error,
      placeholder,
      className,
      ...props
    },
    ref,
  ) => {
    const editor = useEditor({
      enableInputRules: false,
      extensions: [
        StarterKit.configure({
          link: false,
        }),
        RichTextMarkdownPaste,
        Link.configure({
          openOnClick: false,
          autolink: true,
          defaultProtocol: "https",
          protocols: ["http", "https"],
          isAllowedUri: (url, ctx) => {
            try {
              // construct URL
              const parsedUrl = url.includes(":") ? new URL(url) : new URL(`${ctx.defaultProtocol}://${url}`)

              // use default validation
              if (!ctx.defaultValidate(parsedUrl.href)) {
                return false
              }

              // disallowed protocols
              const disallowedProtocols = ["ftp", "file", "mailto"]
              const protocol = parsedUrl.protocol.replace(":", "")

              if (disallowedProtocols.includes(protocol)) {
                return false
              }

              // only allow protocols specified in ctx.protocols
              const allowedProtocols = ctx.protocols.map((p) => (typeof p === "string" ? p : p.scheme))

              if (!allowedProtocols.includes(protocol)) {
                return false
              }

              // all checks have passed
              return true
            } catch {
              return false
            }
          },
          shouldAutoLink: (url) => {
            try {
              // construct URL
              const parsedUrl = url.includes(":") ? new URL(url) : new URL(`https://${url}`)

              // only auto-link if the domain is not in the disallowed list
              const disallowedDomains = [""]
              const domain = parsedUrl.hostname

              return !disallowedDomains.includes(domain)
            } catch {
              return false
            }
          },
        }),
      ],
      content: value,
      onBlur({ editor }) {
        // The content has changed -> update the formik value
        onChange(editor.getHTML())
      },
    })

    // Expose forceNewContent method to parent components
    useImperativeHandle(
      ref,
      () => ({
        forceNewContent: (content: string) => {
          if (editor) {
            editor.commands.setContent(content)
            onChange(content)
          }
        },
      }),
      [editor, onChange],
    )

    return (
      <div className={cn("w-full", className)} {...props}>
        <Text fontStyle="LS" className="flex-1 uppercase">
          {label}
          {isRequiredInput && (
            <Text fontStyle="LL" className="text-textError">
              {" *"}
            </Text>
          )}
        </Text>

        <RichTextMenuBar editor={editor} />
        <EditorContent
          editor={editor}
          className={
            "hu-richtext-input " +
            paragrapheFontStyle +
            (isInvalid ? " aria-invalid:border-borderError! aria-invalid:ring-borderError/20!" : " ") +
            (editor?.isFocused ? " focused" : "")
          }
          maxLength={maxLength}
          placeholder={placeholder}
          onClick={() => {
            editor?.commands.focus()
          }}
          aria-invalid={isInvalid}
        />
        {error}
      </div>
    )
  },
)
